<template>
  <div class="ck" :class="{ 'ck--folded': fold.all }">
    <!-- ============ 顶栏：标识 + 全局合计 ============ -->
    <header v-if="!fold.all || bare" class="ck-top">
      <span class="ck-beam" aria-hidden="true"></span>
      <div class="ck-brand">
        <i class="ck-logo"></i>
        <div class="ck-brand-text">
          <h1>四季青服装街区 <em>·</em> 数字孪生驾驶舱</h1>
          <p>SUIQING GARMENT DISTRICT · DIGITAL TWIN COCKPIT</p>
        </div>
      </div>

      <!-- 时钟 + 日期/天气：从底栏上移，顶栏中央 -->
      <div class="ck-head-time">
        <div class="ck-clock">{{ clock }}</div>
        <div class="ck-date">
          <b>{{ dateText }}</b>
          <span>{{ weekText }} · {{ dist.weather.text }} {{ dist.weather.temp }}℃</span>
        </div>
      </div>

      <div class="ck-head-right">
        <div v-for="k in kpis" :key="k.label" class="ck-kpi">
          <span class="ck-kpi-label">
            <i v-if="k.live" class="ck-live" aria-hidden="true"></i>{{ k.label }}
          </span>
          <b class="ck-kpi-value">{{ k.value }}<i v-if="k.unit">{{ k.unit }}</i></b>
        </div>
        <button type="button" class="ck-fold" :title="fold.all ? '展开顶栏与两侧、底部面板' : '收起顶栏与两侧、底部面板'"
          @click="foldAll">
          {{ fold.all ? '展开面板' : '收起面板' }}
        </button>
      </div>
    </header>

    <!-- 顶栏收起后，那颗收起按钮也跟着没了：在原来的位置留一颗还原钮 -->
    <button v-if="fold.all && !bare" type="button" class="ck-fold ck-fold--restore" title="展开顶栏与两侧、底部面板"
      @click="foldAll">展开面板</button>

    <!-- 列骨架：贯穿左右两列的青色垂线（带流光），把每张卡片"串"在同一根梁上，
         消除"零散浮块"的松散感。与列同进退，收起时一并消失。 -->
    <i v-if="!hide.left" class="ck-spine ck-spine--l" aria-hidden="true"></i>
    <i v-if="!hide.right" class="ck-spine ck-spine--r" aria-hidden="true"></i>

    <!-- ============ 左列 ============ -->
    <aside v-if="!hide.left" class="ck-col ck-col--left">
      <!-- 选中建筑：中间那块是"画布上抠出来的洞"，三维模型由 scissor 视图画在洞底。
           ⚠️ 洞和它的祖先不能有 background / backdrop-filter，否则模型被盖住或糊成毛玻璃，
              所以这张卡片的底色拆在上下两条 .ck-band 上，容器只留一圈描边。 -->
      <section class="ck-card ck-card--cut">
        <header class="ck-t ck-band">
          <h2>建筑详情</h2>
          <span class="ck-note">{{ selectedCode ? selectedCode + ' 号楼' : '未选中' }}</span>
          <button type="button" class="ck-chev" title="收起左栏" @click="fold.left = true">‹</button>
        </header>

        <div ref="viewerEl" class="ck-viewer" :class="{ 'is-orbiting': orbiting, 'is-switching': switching }"
          @pointerdown="onOrbitDown" @pointermove="onOrbitMove" @pointerup="onOrbitUp" @pointercancel="onOrbitUp"
          @lostpointercapture="onOrbitUp">
          <i class="ck-brk ck-brk--tl"></i><i class="ck-brk ck-brk--tr"></i>
          <i class="ck-brk ck-brk--bl"></i><i class="ck-brk ck-brk--br"></i>
          <span class="ck-viewer-tag">{{ selectedCode || '— —' }}</span>
          <span class="ck-viewer-hint">{{ viewerHint }}</span>
          <!-- 未选中空态：HUD 准星 + 旋转标定环 + 扫描线，替代空黑洞 -->
          <div v-if="!selectedCode" class="ck-viewer-empty" aria-hidden="true">
            <i class="ck-ve-ring"></i>
            <i class="ck-ve-cross"></i>
            <b>暂未选中建筑</b>
            <span>点击地图上的建筑模型或名称牌查看详情</span>
          </div>
        </div>

        <div class="ck-pad ck-band" :class="{ 'is-switching': switching }">
          <div class="ck-detail-body">
            <div class="ck-mgrid">
              <div v-for="c in statCards" :key="c.label" class="ck-metric">
                <span>{{ c.label }}<em v-if="c.sim" class="ck-sim">拟</em></span>
                <b>{{ c.value }}<i v-if="c.unit">{{ c.unit }}</i></b>
              </div>
            </div>
            <div class="ck-rent">
              <svg class="ck-ring" viewBox="0 0 72 72">
                <circle class="ck-ring-track" cx="36" cy="36" r="26" fill="none" stroke="rgba(64,150,255,.18)"
                  stroke-width="9" />
                <circle v-if="rentedTotal > 0" class="ck-ring-arc" cx="36" cy="36" r="26" fill="none" stroke="url(#ckRent)"
                  stroke-width="9" stroke-linecap="round" :stroke-dasharray="rentedArc + ' ' + (CIRC - rentedArc)"
                  transform="rotate(-90 36 36)" />
                <defs>
                  <linearGradient id="ckRent" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stop-color="#4fd8ff" />
                    <stop offset="1" stop-color="#2f7bff" />
                  </linearGradient>
                </defs>
                <text x="36" y="34" text-anchor="middle" class="ck-ring-num">{{ rentedRateText }}</text>
                <text x="36" y="46" text-anchor="middle" class="ck-ring-sub">入驻率</text>
              </svg>
              <ul class="ck-legend-v">
                <li v-for="s in rentedLegend" :key="s.name">
                  <i :style="{ background: s.color }"></i><span>{{ s.name }}</span><b>{{ s.value }}</b>
                  <em v-if="s.sim" class="ck-sim">拟</em>
                </li>
              </ul>
            </div>
          </div>
          <p class="ck-intro">{{ introText }}</p>
          <dl class="ck-inline">
            <div><dt>占地</dt><dd>{{ footprintText }}</dd></div>
            <div><dt>本栋业态</dt><dd>{{ ownScopes }}</dd></div>
            <div><dt>WGS84</dt><dd>{{ wgsText }}</dd></div>
            <div><dt>GCJ02</dt><dd>{{ gcjText }}</dd></div>
          </dl>
        </div>
      </section>

      <!-- 建筑索引：23 栋的选中入口之一（另外两个是点模型和点名称牌） -->
      <section class="ck-card">
        <header class="ck-t">
          <h2>建筑索引</h2>
          <span class="ck-note">{{ rows.length }}/{{ buildings.length }}</span>
        </header>
        <div class="ck-pad ck-pad--tight">
          <div class="ck-search">
            <input v-model.trim="keyword" type="search" placeholder="输入楼名或楼号筛选" />
          </div>
          <ul ref="listEl" class="ck-list">
            <li v-for="r in rows" :key="r.code" :class="{ 'is-active': r.code === selectedCode }" @click="pick(r.code)">
              <b class="ck-list-code">{{ r.code }}</b>
              <span class="ck-list-name">{{ r.name }}</span>
              <span class="ck-list-num">{{ r.merchants }}<em v-if="r.sim" class="ck-sim">拟</em></span>
              <i class="ck-list-bar" :style="{ width: r.pct + '%' }"></i>
            </li>
            <li v-if="!rows.length" class="ck-list-empty">没有匹配的建筑</li>
          </ul>
        </div>
      </section>

      <section class="ck-card">
        <header class="ck-t">
          <h2>实时客流趋势</h2>
          <span class="ck-note">分时客流 · 人次<em class="ck-sim">模拟</em></span>
        </header>
        <div class="ck-pad">
          <div class="ck-flow-wrap" @mouseenter="pauseTour" @mouseleave="flowHover = -1; resumeTour()">
            <div class="ck-flow-grid" aria-hidden="true"></div>
            <svg class="ck-flow" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              <defs>
                <linearGradient id="ckFlowFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stop-color="#3fd8ff" stop-opacity="0.42" />
                  <stop offset="1" stop-color="#3fd8ff" stop-opacity="0" />
                </linearGradient>
                <linearGradient id="ckFlowStroke" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stop-color="#2f7bff" />
                  <stop offset="1" stop-color="#4fe4ff" />
                </linearGradient>
              </defs>
              <path class="ck-flow-area" :d="flowArea" fill="url(#ckFlowFill)" />
              <line class="ck-flow-now" :x1="flowNowX" :x2="flowNowX" y1="0" y2="100"
                vector-effect="non-scaling-stroke" />
              <path class="ck-flow-line" :d="flowLine" fill="none" pathLength="100" stroke="url(#ckFlowStroke)"
                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
            </svg>
            <div class="ck-flow-dots" aria-hidden="true">
              <i v-for="p in flowPts" :key="p.i" class="ck-flow-dot"
                :class="{ 'is-now': p.i === dist.peakIndex, 'is-on': p.i === flowHover }"
                :style="{ left: p.x + '%', top: p.y + '%' }"></i>
            </div>
            <div class="ck-flow-zones">
              <i v-for="p in flowPts" :key="'z' + p.i" class="ck-flow-zone" :class="{ 'is-on': p.i === flowHover }"
                @mouseenter="flowHover = p.i" @mousemove="flowHover = p.i"></i>
            </div>
            <div v-if="flowTipPos" class="ck-flow-tip" :style="flowTipPos">
              <b>{{ fmtGrouped(flowTipPoint.value) }}</b>
              <span>{{ flowTipPoint.label }} · 人次</span>
            </div>
          </div>
          <div class="ck-flow-axis">
            <span v-for="(h, i) in dist.hours" :key="h.label" :class="{ 'is-on': i === flowHover }">
              {{ i % 2 === 0 ? h.label.slice(0, 2) : '' }}
            </span>
          </div>
        </div>
      </section>
    </aside>

    <!-- ============ 右列 ============ -->
    <aside v-if="!hide.right" class="ck-col ck-col--right">
      <section class="ck-card">
        <header class="ck-t">
          <h2>实时事件预警</h2>
          <span class="ck-note is-warn">今日 {{ dist.events.length + dist.eventsDone }} 件<em class="ck-sim">模拟</em></span>
        </header>
        <div class="ck-pad">
          <div class="ck-chips">
            <span class="ck-chip lv-high"><i class="ck-dot"></i>待处置<b>{{ pendingCount }}</b></span>
            <span class="ck-chip lv-mid"><i class="ck-dot"></i>处理中<b>{{ handlingCount }}</b></span>
            <span class="ck-chip lv-low"><i class="ck-dot"></i>已完结<b>{{ dist.eventsDone }}</b></span>
          </div>
          <ul class="ck-events">
            <li v-for="(e, i) in dist.events" :key="i" :class="'lv-' + e.level"
              :style="{ animationDelay: 0.06 + i * 0.07 + 's' }">
              <i class="ck-dot"></i>
              <span class="ck-ev-text">{{ e.text }}</span>
              <b class="ck-ev-time">{{ ago(e.mins) }}</b>
              <em class="ck-ev-state">{{ e.state }}</em>
            </li>
          </ul>
          <!-- 主力业态摘要：从"业态结构分布"卡迁来，贴卡底常驻；
               该卡弹性伸展时，富余高度由列表与此行之间的空当吸收 -->
          <div class="ck-scope-biz">
            <span class="ck-scope-biz-label">主力业态</span>
            <template v-if="scopeTop3.length">
              <span v-for="s in scopeTop3" :key="s.name" class="ck-scope-biz-item">
                <i :style="{ background: s.color }"></i>{{ s.name }} <b>{{ Math.round(s.pct) }}%</b>
              </span>
              <em v-if="scopeSim" class="ck-sim">模拟</em>
            </template>
            <span v-else class="ck-scope-biz-item">暂无业态数据</span>
          </div>
        </div>
      </section>

      <section class="ck-card">
        <header class="ck-t">
          <h2>业态结构分布</h2>
          <span class="ck-note">在营 {{ fmtGrouped(scopeTotal) }} 家</span>
        </header>
        <div class="ck-pad">
          <div class="ck-scope" @mouseenter="pauseTour" @mouseleave="scopeHover = -1; resumeTour()">
            <div class="ck-donut-wrap" :class="{ 'is-hovering': scopeHover >= 0 }">
              <svg class="ck-donut" viewBox="0 0 100 100" aria-hidden="true">
                <circle class="ck-donut-track" cx="50" cy="50" :r="DONUT_R" />
                <circle v-for="s in donutSegs" :key="s.name" class="ck-donut-seg" cx="50" cy="50" :r="DONUT_R"
                  :stroke="s.color" :stroke-dasharray="s.dash + ' ' + (DONUT_C - s.dash)"
                  :stroke-dashoffset="s.offset" :class="{ 'is-on': scopeHover === s.i, 'is-dim': scopeHover >= 0 && scopeHover !== s.i }"
                  transform="rotate(-90 50 50)" @mouseenter="scopeHover = s.i" @mouseleave="scopeHover = -1" />
              </svg>
              <div class="ck-donut-center">
                <b>{{ fmtGrouped(scopeTotal) }}</b>
                <span>家在营</span>
              </div>
            </div>
            <div class="ck-scope-side">
              <ul class="ck-legend">
                <li v-for="s in donutSegs" :key="s.name" :class="{ 'is-on': scopeHover === s.i }"
                  @mouseenter="scopeHover = s.i" @mouseleave="scopeHover = -1">
                  <i :style="{ background: s.color }"></i><span>{{ s.name }}</span><b>{{ Math.round(s.pct) }}%</b>
                </li>
                <li v-if="!donutSegs.length" class="ck-legend-empty">暂无业态数据</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section class="ck-card">
        <header class="ck-t">
          <h2>环境与能耗</h2>
          <span class="ck-note">今日累计<em class="ck-sim">模拟</em></span>
        </header>
        <div class="ck-pad">
          <div class="ck-mgrid">
            <div v-for="t in envTiles" :key="t.label" class="ck-metric" :class="'tone-' + t.tone">
              <span>{{ t.label }}</span>
              <b>{{ t.value }}<i>{{ t.unit }}</i></b>
              <span class="ck-mbar"><i :style="{ width: t.pct + '%' }"></i></span>
            </div>
          </div>
        </div>
      </section>

      <section class="ck-card">
        <header class="ck-t">
          <h2>重点场馆运行</h2>
          <span class="ck-note is-ok">全部正常<em class="ck-sim">模拟</em></span>
        </header>
        <div class="ck-pad">
          <ul class="ck-load">
            <li v-for="v in dist.venues" :key="v.name">
              <span>{{ v.name }} · {{ v.metric }}</span>
              <i class="ck-track"><i class="ck-fill" :class="{ 'is-hot': v.pct >= 90 }"
                  :style="{ width: v.pct + '%' }"></i></i>
              <b>{{ v.pct }}%</b>
            </li>
          </ul>
        </div>
      </section>

      <section class="ck-card">
        <header class="ck-t">
          <h2>系统运行状态</h2>
          <span class="ck-note is-ok">在线率 {{ dist.system.online }}%<em class="ck-sim">模拟</em></span>
        </header>
        <div class="ck-pad">
          <ul class="ck-sys">
            <li v-for="s in sysItems" :key="s.label" :class="'tone-' + s.tone">
              <span class="ck-sys-ring">
                <svg viewBox="0 0 40 40" aria-hidden="true">
                  <circle class="ck-ring-track" cx="20" cy="20" :r="SYS_R" />
                  <circle class="ck-ring-arc" cx="20" cy="20" :r="SYS_R"
                    :stroke-dasharray="sysArc(s.pct) + ' ' + SYS_C" transform="rotate(-90 20 20)" />
                </svg>
                <b>{{ s.pctLabel }}</b>
              </span>
              <span class="ck-sys-meta">
                <span class="ck-sys-label">{{ s.label }}</span>
                <b class="ck-sys-value">{{ s.value }}</b>
              </span>
              <em class="ck-sys-tag">{{ s.tag }}</em>
            </li>
          </ul>
        </div>
      </section>
    </aside>

    <!-- ============ 地图上的图层开关（竖排，贴在左列右边） ============ -->
    <nav v-if="!hide.left" class="ck-layers">
      <button type="button" @click="$emit('reset-view')">复位</button>
      <button type="button" :class="{ 'is-on': contextOn }" @click="$emit('update:context', !contextOn)">周边</button>
      <button type="button" :class="{ 'is-on': labelsOn }" @click="$emit('update:labels', !labelsOn)">标牌</button>
      <button type="button" :class="{ 'is-on': calibOpen }" @click="$emit('update:calib', !calibOpen)">校准</button>
    </nav>

    <!-- ============ 底部经营监测面板：横跨左右两列之间，填充地图下缘 ============
         三个贴合街区业态的模拟图表（近 7 日客流 / 品类销售排行 / 智慧停车），
         数据由顶栏同一份模拟口径派生，同一天内不跳数；点击建筑时相机落点已上移，
         不会挡住选中的楼。随左右列与底栏一同收起。 -->
    <section v-if="!hide.bottom && !hide.left && !hide.right" class="ck-card ck-biz-panel">
      <header class="ck-t">
        <h2>街区经营监测</h2>
        <span class="ck-note">近 7 日 · 模拟口径<em class="ck-sim">拟</em></span>
      </header>
      <div class="ck-biz-cols">
        <!-- 近 7 日客流柱图：今日那根用真实累计口径，其余按周内波形派生 -->
        <div class="ck-biz-col ck-biz-col--bars">
          <div class="ck-biz-title">近 7 日客流</div>
          <div class="ck-bars7">
            <div v-for="d in weekFlow" :key="d.label" class="ck-bar7" :class="{ 'is-today': d.isToday, 'is-peak': d.isPeak }">
              <i class="ck-bar7-val">{{ fmtGrouped(d.value) }}</i>
              <i class="ck-bar7-pill" :style="{ height: d.pct + '%' }"></i>
              <span>{{ d.label }}</span>
            </div>
          </div>
        </div>
        <!-- 品类销售排行：按业态构成 × 固定客单系数派生的日销估计 -->
        <div class="ck-biz-col ck-biz-col--rank">
          <div class="ck-biz-title">品类销售排行<span class="ck-biz-unit">万元 / 日</span></div>
          <ul class="ck-rank5">
            <li v-for="(r, i) in salesRank" :key="r.name">
              <i class="ck-rank-idx" :class="{ 'is-top': i === 0 }">{{ i + 1 }}</i>
              <span class="ck-rank-name">{{ r.name }}</span>
              <div class="ck-rank-track"><i :style="{ width: r.pct + '%', background: r.color }"></i></div>
              <b class="ck-rank-num">{{ r.sales.toFixed(1) }}</b>
            </li>
          </ul>
        </div>
        <!-- 智慧停车：占用环形 + 余位读数 -->
        <div class="ck-biz-col ck-biz-col--park">
          <div class="ck-biz-title">智慧停车</div>
          <div class="ck-park">
            <svg viewBox="0 0 84 84" aria-hidden="true">
              <circle class="ck-park-track" cx="42" cy="42" r="34" />
              <circle class="ck-park-arc" cx="42" cy="42" r="34" :stroke-dasharray="parkArc + ' ' + PARK_C"
                transform="rotate(-90 42 42)" />
            </svg>
            <div class="ck-park-center">
              <b>{{ parkPct }}%</b>
              <span>占用率</span>
            </div>
          </div>
          <dl class="ck-park-meta">
            <div><dt>余位</dt><dd>{{ fmtGrouped(districtTele.parking.free) }}</dd></div>
            <div><dt>总位</dt><dd>{{ fmtGrouped(districtTele.parking.total) }}</dd></div>
          </dl>
        </div>
      </div>
    </section>

    <!-- ============ 底栏：视角读数 + 口径 ============ -->
    <footer v-if="!hide.bottom" class="ck-bottom">
      <div class="ck-legend-view">
        <span>视角</span>
        <i>缩放 <b>{{ num(view.zoom, 2) }}</b></i>
        <i>俯仰 <b>{{ deg(view.pitch) }}</b></i>
        <i>方位 <b>{{ deg(view.rotation) }}</b></i>
      </div>
      <div class="ck-source">
        <span>数据来源：区域概览接口（kh-cockpit）· 标注<em class="ck-sim">拟</em>的为模拟数据</span>
        <button type="button" class="ck-wm" :class="{ 'is-on': watermarkOn }"
          :title="watermarkOn ? '关闭全屏水印' : '开启全屏水印'" @click="$emit('update:watermark', !watermarkOn)">水印</button>
        <button type="button" class="ck-chev" :title="fold.bottom ? '展开' : '收起'"
          @click="fold.bottom = !fold.bottom">{{ fold.bottom ? '▴' : '▾' }}</button>
      </div>
    </footer>

    <!-- ============ 收起后的边缘把手 ============ -->
    <button v-if="fold.left && !bare" type="button" class="ck-tab ck-tab--left" @click="unfold('left')">建筑详情</button>
    <button v-if="fold.right && !bare" type="button" class="ck-tab ck-tab--right" @click="unfold('right')">运行态势</button>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { AREA_ID_BY_BUILDING, fetchAreaOverview } from '@/services/areaOverview'
import { districtTelemetry, pickNum, telemetryOf } from '@/services/mockTelemetry'
import { wgs2gcj } from '@/three/geo'

const props = defineProps({
  /** [{code, name}] —— 23 栋，由场景侧的标牌清单给出 */
  buildings: { type: Array, default: () => [] },
  selectedCode: { type: String, default: '' },
  /** sceneView 的 emitSelection 载荷（含 lla / sphereRadius / footprintRadius） */
  selected: { type: Object, default: null },
  view: { type: Object, default: () => ({}) },
  contextOn: { type: Boolean, default: true },
  labelsOn: { type: Boolean, default: true },
  calibOpen: { type: Boolean, default: false },
  watermarkOn: { type: Boolean, default: true },
  hasModel: { type: Boolean, default: true },
  /** 只留顶栏，其余全收（截图/演示用） */
  bare: { type: Boolean, default: false },
})

const emit = defineEmits([
  'select',
  'detail-slot',
  'reset-view',
  'update:context',
  'update:labels',
  'update:calib',
  'update:watermark',
  'orbit',
  'orbit-start',
  'orbit-end',
])

const SCOPE_COLORS = ['#3fd8ff', '#2f7bff', '#2ee6a6', '#f5a623', '#8b7bff', '#ff5f7e']
const RING_R = 26
// 环形图几何：viewBox 72 / 半径 26 -> 周长约 163.4
const CIRC = Math.round(2 * Math.PI * RING_R * 10) / 10

const viewerEl = ref(null)
const listEl = ref(null)
/** 默认收起：进页面只看到三维街区，顶栏和面板都等用户自己拉出来 */
const fold = reactive({ left: true, right: true, bottom: true, all: true })
/** bare 只留顶栏（截图/演示用），和手动收起走同一条判断 */
const hide = computed(() => ({
  left: fold.left || props.bare,
  right: fold.right || props.bare,
  bottom: fold.bottom || props.bare,
}))
const keyword = ref('')
/** 街区级仪表的模拟数据：种子是"今天"，同一天内不跳数 */
const dist = reactive(districtTelemetry())

function pick(code) {
  emit('select', code)
}

function foldAll() {
  fold.all = !fold.all
  fold.left = fold.all
  fold.right = fold.all
  fold.bottom = fold.all
}

/** 从边缘把手单开一栏：顶栏不属于任何一栏，只要不再是全收状态就回来 */
function unfold(side) {
  fold[side] = false
  fold.all = false
}

// ---- 详情窗口里的拖拽轨道：只报位移增量，角度与惯性在 buildingDetailView 里算 ----
const orbiting = ref(false)
let orbitLast = null

// ---- 切换建筑时的"扫描刷新"动画：楼号一变就播一次（含从空态到首次选中）----
const switching = ref(false)
let switchTimer = 0

watch(
  () => props.selectedCode,
  (v, o) => {
    if (v === o) return
    switching.value = true
    clearTimeout(switchTimer)
    switchTimer = setTimeout(() => {
      switching.value = false
    }, 720)
  },
)

function onOrbitDown(e) {
  if (!props.selectedCode || !props.hasModel) return
  e.stopPropagation()
  // 捕获指针：拖出这块窗口甚至松手在面板外，也还能继续转
  if (e.currentTarget.setPointerCapture) e.currentTarget.setPointerCapture(e.pointerId)
  orbiting.value = true
  orbitLast = { x: e.clientX, y: e.clientY }
  emit('orbit-start')
}

function onOrbitMove(e) {
  if (!orbiting.value || !orbitLast) return
  e.stopPropagation()
  const dx = e.clientX - orbitLast.x
  const dy = e.clientY - orbitLast.y
  orbitLast = { x: e.clientX, y: e.clientY }
  if (dx || dy) emit('orbit', dx, dy)
}

function onOrbitUp(e) {
  if (!orbiting.value) return
  if (e && e.stopPropagation) e.stopPropagation()
  orbiting.value = false
  orbitLast = null
  emit('orbit-end')
}

const viewerHint = computed(() => {
  if (!props.selectedCode) return '点击建筑查看详情'
  if (!props.hasModel) return '该楼无独立单体模型'
  return orbiting.value ? '拖拽中 · 松手继续自转' : '按住拖拽可旋转模型'
})

// ---- 底栏时钟 ----
const now = ref(Date.now())
let timer = 0
onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now()
  }, 1000)
})
onBeforeUnmount(() => {
  clearInterval(timer)
  clearInterval(tourTimer)
  clearTimeout(switchTimer)
  clearTimeout(tourResumeTimer)
})

const pad2 = (v) => String(v).padStart(2, '0')
const clock = computed(() => {
  const d = new Date(now.value)
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
})
const dateText = computed(() => {
  const d = new Date(now.value)
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
})
const weekText = computed(() => '星期' + '日一二三四五六'.charAt(new Date(now.value).getDay()))

/** 事件时间：把"多少分钟前"换算成钟点，大屏上读钟点比读时长快 */
function ago(mins) {
  const d = new Date(now.value - mins * 60000)
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

// ---- 数字格式化 ----
function fmtWan(v) {
  const n = Number(v)
  if (v === null || v === undefined || v === '' || !Number.isFinite(n)) return null
  return { value: (Math.round((n / 10000) * 10) / 10).toFixed(1), unit: 'w' }
}

function fmtCount(v) {
  if (v === null || v === undefined || v === '') return '—'
  const n = Number(v)
  return Number.isFinite(n) ? String(Math.round(n)) : '—'
}

function fmtGrouped(v) {
  const n = Number(v)
  if (!Number.isFinite(n)) return '—'
  return Math.round(n).toLocaleString('en-US')
}

// 0 是合法读数，不能用三元判 falsy（方位 0° 会被画成"—"）
const num = (v, d) => (Number.isFinite(Number(v)) ? Number(v).toFixed(d) : '—')
const deg = (v) => (Number.isFinite(Number(v)) ? Number(v).toFixed(0) + '°' : '—')

// ---- 区域概览：23 个区一次并发拉完缓存住，左右两栏与顶栏吃同一份 ----
const byName = reactive({})
// 拉过就认账（含失败的）：不记这个，失败的楼会被反复重请求
const attempted = new Set()
const loadingAll = ref(true)

async function loadAll() {
  // 清单是模型加载完才有的（onMounted 时还是空数组），所以每次长度变化都补一轮
  const queue = props.buildings.filter((b) => AREA_ID_BY_BUILDING[b.name] && !attempted.has(b.name))
  if (!queue.length) {
    loadingAll.value = false
    return
  }
  loadingAll.value = true
  // 4 个一批：外部接口是免登录的只读口，全并发会互相排队，也没必要
  for (let i = 0; i < queue.length; i += 4) {
    const batch = queue.slice(i, i + 4)
    await Promise.all(
      batch.map(async (b) => {
        attempted.add(b.name)
        try {
          byName[b.name] = await fetchAreaOverview(AREA_ID_BY_BUILDING[b.name])
        } catch (e) {
          byName[b.name] = null // 拉不到就按"无数据"展示，不让整块面板挂掉
        }
      }),
    )
  }
  loadingAll.value = false
}

onMounted(loadAll)
// 建筑清单是异步加载完才灌进来的，等它
watch(() => props.buildings.length, loadAll)

const detail = computed(() => (props.selected ? byName[props.selected.name] || null : null))
/** 没选中任何一栋时，能汇总真数据的卡片按"全街区"口径走，别让仪表空着 */
const agg = computed(() => {
  const out = { rented: 0, vacant: 0, merchants: 0, scopes: [] }
  const map = {}
  props.buildings.forEach((b) => {
    const d = byName[b.name]
    if (!d) return
    out.merchants += Number(d.totalMerchants) || 0
    out.rented += Number(d.rentedCount) || 0
    out.vacant += Number(d.vacantCount) || 0
    const list = Array.isArray(d.businessScopes) ? d.businessScopes : []
    list.forEach((s) => {
      if (!s || !s.name) return
      map[s.name] = (map[s.name] || 0) + (Number(s.shopCount) || 0)
    })
  })
  out.scopes = Object.keys(map)
    .map((name) => ({ name, shopCount: map[name] }))
    .sort((a, b) => b.shopCount - a.shopCount)
  return out
})

/** 本栋的仪表数据（没有真实来源的那几项按楼号定种子，切走再切回来不跳数） */
const tele = computed(() =>
  props.selectedCode ? telemetryOf(props.selectedCode, (props.selected && props.selected.name) || '') : telemetryOf('district', '全街区'),
)
/** 顶栏是全局口径，不能跟着选中的楼跳数 */
const districtTele = computed(() => telemetryOf('district', '全街区'))

const kpis = computed(() => {
  let merchants = 0
  let area = 0
  let staff = 0
  props.buildings.forEach((b) => {
    const d = byName[b.name]
    if (!d) return
    merchants += Number(d.totalMerchants) || 0
    area += Number(d.operatingArea) || 0
    staff += Number(d.practitioner) || 0
  })
  // 一项都没汇总出来（接口全空或全挂）时，合计只能是模拟口径，标出来别当数看
  const sim = !merchants && !area && !staff
  const fb = telemetryOf('0', '街区合计').fallback
  const wan = fmtWan(sim ? fb.operatingArea * props.buildings.length : area)
  return [
    { label: '重点建筑', value: String(props.buildings.length), unit: '栋' },
    { label: '在营商户', value: fmtGrouped(sim ? fb.totalMerchants * props.buildings.length : merchants), unit: '家', sim },
    { label: '经营面积', value: wan ? wan.value : '—', unit: wan ? wan.unit + '㎡' : '', sim },
    { label: '实时客流', value: fmtGrouped(districtTele.value.flowToday), unit: '人', sim: true, live: true },
  ]
})

const statCards = computed(() => {
  const d = detail.value
  const fb = tele.value.fallback
  const area = pickNum(d && d.operatingArea, fb.operatingArea)
  const merch = pickNum(d && d.totalMerchants, fb.totalMerchants)
  const staff = pickNum(d && d.practitioner, fb.practitioner)
  // 昨日客流无上报时接口回 0，那是正常值，不当缺数处理
  const flow = pickNum(d && d.yesterdayNumber, tele.value.yesterdayFlow)
  const wan = fmtWan(area.value)
  return [
    { label: '经营面积', value: wan ? wan.value : '—', unit: wan ? wan.unit + '㎡' : '', sim: area.sim },
    { label: '商户数', value: fmtCount(merch.value), unit: '家', sim: merch.sim },
    { label: '从业人员', value: fmtCount(staff.value), unit: '人', sim: staff.sim },
    { label: '昨日客流', value: fmtCount(flow.value), unit: '人', sim: flow.sim },
  ]
})

const introText = computed(() => {
  if (loadingAll.value && !detail.value) return '区域数据加载中…'
  const d = detail.value
  if (d && d.areaIntro) return d.areaIntro
  if (d && d.areaName) return `${d.areaName}暂无区域介绍。`
  if (!props.selected) return '点击地图上的建筑模型或名称牌，中间这块窗会把那栋楼搬进来。'
  return `${props.selected.name}（${props.selected.code} 号楼）暂无区域数据。`
})

/** 业态结构分布这张卡固定是"全街区"口径；本栋自己的业态写在详情卡里，不混着看 */
const scopeBars = computed(() => {
  const list = agg.value.scopes
  const src = list.length ? list : tele.value.scopes
  const sum = Math.max(1, src.reduce((s, it) => s + (Number(it.shopCount) || 0), 0))
  return src
    .map((it, i) => ({
      name: it.name || '其他',
      value: Number(it.shopCount) || 0,
      pct: ((Number(it.shopCount) || 0) / sum) * 100,
      color: SCOPE_COLORS[i % SCOPE_COLORS.length],
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6)
})

const scopeTotal = computed(() => scopeBars.value.reduce((s, it) => s + it.value, 0))
const scopeSim = computed(() => !agg.value.scopes.length)
/** 占比前三的业态：摘要在"实时事件预警"卡里常驻展示 */
const scopeTop3 = computed(() => scopeBars.value.slice(0, 3))

/** 选中那栋自己的经营品类，一行带过 */
const ownScopes = computed(() => {
  const d = detail.value
  const list = d && Array.isArray(d.businessScopes) ? d.businessScopes : []
  if (!list.length) return '—'
  const sum = Math.max(1, list.reduce((s, it) => s + (Number(it.shopCount) || 0), 0))
  return list
    .slice(0, 3)
    .map((it) => `${it.name || '其他'} ${Math.round(((Number(it.shopCount) || 0) / sum) * 100)}%`)
    .join(' · ')
})

/** 建筑索引：商户数是真的，接口没给的那几栋用模拟兜底并标出来 */
const rows = computed(() => {
  const kw = keyword.value
  const vals = props.buildings.map((b) => {
    const d = byName[b.name]
    const n = Number(d && d.totalMerchants)
    return Number.isFinite(n) && n > 0 ? n : telemetryOf(b.code, b.name).fallback.totalMerchants
  })
  const max = Math.max(1, ...vals)
  return props.buildings
    .map((b, i) => {
      const d = byName[b.name]
      const real = Number(d && d.totalMerchants)
      const has = Number.isFinite(real) && real > 0
      return {
        code: b.code,
        name: b.name,
        merchants: fmtGrouped(vals[i]),
        sim: !has,
        pct: Math.round((vals[i] / max) * 100),
      }
    })
    .filter((r) => !kw || r.name.indexOf(kw) >= 0 || r.code === kw)
})

/** 入驻/空置：选中看本栋，未选中看全街区汇总，两头都没有才是模拟 */
const rentPair = computed(() => {
  if (props.selectedCode) {
    const d = detail.value
    const a = Number(d && d.rentedCount)
    const b = Number(d && d.vacantCount)
    if (d && Number.isFinite(a) && Number.isFinite(b) && a + b > 0) return { rented: a, vacant: b, sim: false }
  } else if (agg.value.rented + agg.value.vacant > 0) {
    return { rented: agg.value.rented, vacant: agg.value.vacant, sim: false }
  }
  const fb = tele.value.fallback
  return { rented: fb.rentedCount, vacant: fb.vacantCount, sim: true }
})

const rentedTotal = computed(() => rentPair.value.rented + rentPair.value.vacant)
const rentedArc = computed(() =>
  rentedTotal.value > 0 ? (rentPair.value.rented / rentedTotal.value) * CIRC : 0,
)
const rentedRateText = computed(() =>
  rentedTotal.value > 0 ? Math.round((rentPair.value.rented / rentedTotal.value) * 100) + '%' : '—',
)
const rentedLegend = computed(() => [
  { name: '已租', value: fmtCount(rentPair.value.rented), color: '#4fd8ff', sim: rentPair.value.sim },
  { name: '空置', value: fmtCount(rentPair.value.vacant), color: 'rgba(127,151,184,.6)', sim: rentPair.value.sim },
])

// ==========================================================================
// 图表数据（全部由已有的模拟/真实字段派生，只做展示，不改任何业务逻辑）
// ==========================================================================

// ---- 分时客流：面积图。viewBox 固定 100×100 + preserveAspectRatio=none，
//      描边用 non-scaling-stroke 保持 2px 不变形；数据点用百分比定位的 HTML 圆点，
//      这样坐标和 SVG 路径严格对齐又不会被拉伸成椭圆。----
const FLOW_TOP = 10
const FLOW_BOTTOM = 16

const flowMax = computed(() => Math.max(1, ...dist.hours.map((h) => h.value)))

const flowPts = computed(() => {
  const list = dist.hours
  // x 统一取"档位中心"（(i+0.5)/12）：高亮竖线画在每个 zone 的正中、轴刻度也是
  // 12 等分 —— 点若用 i/(n-1) 两端顶满，就会和竖线/刻度永久错开半格（实测踩过）
  return list.map((h, i) => ({
    i,
    label: h.label,
    value: h.value,
    x: ((i + 0.5) / Math.max(1, list.length)) * 100,
    y: FLOW_TOP + (1 - h.value / flowMax.value) * (100 - FLOW_TOP - FLOW_BOTTOM),
  }))
})

/** Catmull-Rom → 三次贝塞尔，把折线画成平滑曲线 */
function smoothPath(pts) {
  if (!pts.length) return ''
  if (pts.length < 2) return `M ${pts[0].x} ${pts[0].y}`
  let d = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] || p2
    d +=
      ` C ${p1.x + (p2.x - p0.x) / 6} ${p1.y + (p2.y - p0.y) / 6}` +
      ` ${p2.x - (p3.x - p1.x) / 6} ${p2.y - (p3.y - p1.y) / 6}` +
      ` ${p2.x} ${p2.y}`
  }
  return d
}

const flowLine = computed(() => smoothPath(flowPts.value))
const flowArea = computed(() => {
  const pts = flowPts.value
  if (!pts.length) return ''
  return `${smoothPath(pts)} L ${pts[pts.length - 1].x} 100 L ${pts[0].x} 100 Z`
})

const flowNowX = computed(() => {
  const pts = flowPts.value
  if (!pts.length) return 0
  return pts[Math.min(pts.length - 1, Math.max(0, dist.peakIndex))].x
})

const flowHover = ref(-1)
const flowTipPoint = computed(() => (flowHover.value >= 0 ? flowPts.value[flowHover.value] : null))
/** 气泡横向跟随，两端做夹取，避免被卡片 overflow 裁掉 */
const flowTipPos = computed(() => {
  const p = flowTipPoint.value
  if (!p) return null
  return { left: Math.min(89, Math.max(11, p.x)) + '%' }
})

// ---- 业态结构：环形图。用 stroke-dasharray/offset 拼弧段，hover 时联动放大 ----
const DONUT_R = 38
const DONUT_C = Math.round(2 * Math.PI * DONUT_R * 10) / 10
const scopeHover = ref(-1)

const donutSegs = computed(() => {
  const list = scopeBars.value
  const gap = list.length > 1 ? DONUT_C * 0.012 : 0
  let acc = 0
  return list.map((s, i) => {
    const frac = s.pct / 100
    const seg = {
      ...s,
      i,
      dash: Math.max(0.6, frac * DONUT_C - gap),
      offset: -acc * DONUT_C,
    }
    acc += frac
    return seg
  })
})

// ---- 系统运行状态：四个小环形仪表 ----
const SYS_R = 15
const SYS_C = Math.round(2 * Math.PI * SYS_R * 10) / 10
const sysArc = (pct) => Math.round((Math.max(0, Math.min(100, pct)) / 100) * SYS_C * 10) / 10

const sysItems = computed(() => {
  const s = dist.system
  return [
    { label: '物联感知', value: fmtGrouped(s.iot), pct: s.online, pctLabel: s.online + '%', tag: '在线', tone: 'ok' },
    { label: '视频监控', value: fmtGrouped(s.video), pct: s.online, pctLabel: s.online + '%', tag: '在线', tone: 'ok' },
    { label: '数据中台', value: '同步', pct: 100, pctLabel: '100%', tag: '正常', tone: 'ok' },
    { label: '边缘计算', value: s.edge + '%', pct: s.edge, pctLabel: s.edge + '%', tag: '负载', tone: s.edge >= 75 ? 'warn' : 'ok' },
  ]
})

// ---- 环境与能耗：四格指标 + 底部微条 ----
const envTiles = computed(() => {
  const e = dist.env
  const pct = (v, max) => Math.max(8, Math.min(100, Math.round((Number(v) / max) * 100)))
  return [
    { label: '实时温度', value: e.temp, unit: '℃', pct: pct(e.temp, 40), tone: 'temp' },
    { label: '相对湿度', value: e.humidity, unit: '%RH', pct: pct(e.humidity, 100), tone: 'hum' },
    { label: '今日用电', value: fmtGrouped(e.power), unit: 'kWh', pct: pct(e.power, 32000), tone: 'pow' },
    { label: '今日用水', value: fmtGrouped(e.water), unit: 't', pct: pct(e.water, 620), tone: 'wat' },
  ]
})

// ---- 底部经营监测面板的数据（全部由既有模拟口径派生，系数固定 => 同一天内不跳数）----

// 周内客流波形（周一…周日，周末高）：今日那根用真实累计，其余按今日归一派生
const WEEK_SHAPE = [0.82, 0.9, 0.86, 0.94, 1.1, 1.22, 0.98]
const WEEK_LABELS = ['一', '二', '三', '四', '五', '六', '日']

const weekFlow = computed(() => {
  const today = districtTele.value.flowToday
  const todayIdx = (new Date(now.value).getDay() + 6) % 7 // 周一=0
  const vals = WEEK_SHAPE.map((k, i) => (i === todayIdx ? today : Math.round((today / WEEK_SHAPE[todayIdx]) * k)))
  const max = Math.max(1, ...vals)
  let peak = 0
  vals.forEach((v, i) => {
    if (v > vals[peak]) peak = i
  })
  return vals.map((v, i) => ({
    label: WEEK_LABELS[i],
    value: v,
    pct: Math.max(6, Math.round((v / max) * 100)),
    isToday: i === todayIdx,
    isPeak: i === peak,
  }))
})

/** 品类日销估计：业态商户数 × 固定客单系数（头部递减），名称/颜色与业态环一致 */
const salesRank = computed(() => {
  const K = [1, 0.84, 0.71, 0.6, 0.52]
  const list = scopeBars.value.slice(0, 5).map((s, i) => ({
    name: s.name,
    color: s.color,
    sales: Math.round(s.value * 0.021 * K[i] * 100) / 10,
  }))
  const max = Math.max(1, ...list.map((s) => s.sales))
  list.forEach((s) => {
    s.pct = Math.max(8, Math.round((s.sales / max) * 100))
  })
  return list
})

// 智慧停车占用环：r=34 → 周长 213.6
const PARK_C = Math.round(2 * Math.PI * 34 * 10) / 10
const parkPct = computed(() => {
  const p = districtTele.value.parking
  if (!p.total) return 0
  return Math.max(0, Math.min(100, Math.round(((p.total - p.free) / p.total) * 100)))
})
const parkArc = computed(() => (parkPct.value / 100) * PARK_C)

const pendingCount = computed(() => dist.events.filter((e) => e.state === '待处置').length)
const handlingCount = computed(() => dist.events.filter((e) => e.state === '处理中').length)

// ---- 图表 tooltip 自动轮播：无操作时定时推进客流图与业态图的指向项，
//      数据点/扇区/图例/气泡按既有的 hover 联动样式自动展示。 ----
// 用户悬停任一图即暂停，移出 4s 后恢复；系统开了"减弱动效"就不启动。
const tourPaused = ref(false)
let tourTimer = 0
let tourResumeTimer = 0

function tourStep() {
  if (tourPaused.value) return
  flowHover.value = (flowHover.value + 1) % Math.max(1, dist.hours.length)
  const n = donutSegs.value.length
  if (n) scopeHover.value = (scopeHover.value + 1) % n
}

function pauseTour() {
  tourPaused.value = true
  clearTimeout(tourResumeTimer)
}

function resumeTour() {
  clearTimeout(tourResumeTimer)
  tourResumeTimer = setTimeout(() => {
    tourPaused.value = false
  }, 4000)
}

onMounted(() => {
  // 轮播属于信息展示（不用鼠标也能读到每项数据），不受"减弱动效"降级影响；
  // reduce 下 CSS 过渡/动画已被全局禁用，这里的推进只是瞬时状态切换。
  tourTimer = setInterval(tourStep, 2000)
})

const footprintText = computed(() => {
  const s = props.selected
  if (!s) return '—'
  const r = s.footprintRadius || s.sphereRadius
  if (!r) return '—'
  return `${Math.round(r * 2)} m${s.footprintRadius ? '' : '（估算）'}`
})

const wgsText = computed(() => {
  const l = props.selected && props.selected.lla
  if (!l || !Number.isFinite(l.lat)) return '—'
  return `${l.lat.toFixed(5)}, ${l.lon.toFixed(5)}`
})

const gcjText = computed(() => {
  const l = props.selected && props.selected.lla
  if (!l || !Number.isFinite(l.lon)) return '—'
  const g = wgs2gcj(l.lon, l.lat)
  return `${g[1].toFixed(5)}, ${g[0].toFixed(5)}`
})

// 槽位在"左栏展开"时才存在；元素一出现/一换楼就通知外层重挂三维视图。
// ⚠️ 必须 flush:'post' —— 默认的 pre 回调跑在 DOM 更新之前，那时 ref 还是 null，
// 会先对外广播一次"槽位没了"，把刚挂上的视图又收掉。
watch(
  () => [viewerEl.value, props.selectedCode],
  () => emit('detail-slot', viewerEl.value),
  { flush: 'post' },
)

// 从地图上点中的楼，索引里那一行可能滚出可视区：把它带回来，两边对得上
watch(
  () => props.selectedCode,
  () => {
    const li = listEl.value && listEl.value.querySelector('.is-active')
    if (li && li.scrollIntoView) li.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  },
  { flush: 'post' },
)

// 左栏重新展开时槽位是新元素，等一帧再报，否则外层挂不上
watch(
  () => fold.left,
  (v) => {
    if (!v) nextTick(() => emit('detail-slot', viewerEl.value))
  },
)
</script>

<style scoped>
/* ==========================================================================
   大屏外框：顶栏 + 左右两列卡片 + 底栏，中间留给三维地图。
   视觉语言：深空蓝玻璃面板 + 淡青描边 + 网格纹理 + 辉光，标题前一根青色竖杠。
   ⚠️ .ck 根节点本身绝不加 background / backdrop-filter ——
      建筑详情那张卡中间是"画布上抠出来的洞"（.ck-viewer），
      根节点一旦上底色，scissor 画进去的三维模型就被盖住了。
   ========================================================================== */

.ck {
  --panel: linear-gradient(180deg, rgba(11, 26, 50, 0.92) 0%, rgba(6, 15, 32, 0.88) 100%);
  --grid: repeating-linear-gradient(0deg, rgba(63, 216, 255, 0.05) 0 1px, transparent 1px 24px),
    repeating-linear-gradient(90deg, rgba(63, 216, 255, 0.05) 0 1px, transparent 1px 24px);
  --line: rgba(64, 150, 255, 0.24);
  --line-2: rgba(63, 216, 255, 0.5);
  --txt: #eaf3ff;
  --txt-2: #b9cde8;
  --mut: #7f97b8;
  --cyan: #3fd8ff;
  --blue: #2f7bff;
  --amber: #f5a623;
  --green: #2ee6a6;
  --red: #ff5f7e;
  --violet: #8b7bff;
  --col-w: 464px;
  --gap: 10px;
  /* 底部条带统一高度：左"实时客流趋势" / 中"街区经营监测" / 右"系统运行状态"
     三卡共用 —— 底部同贴底栏上沿、高度一致，顶部自然连成一条水平线 */
  --dock-h: 180px;
  --r: 10px;
  --font-num: 'Bahnschrift', 'DIN Alternate', 'Rajdhani', 'Microsoft YaHei', sans-serif;
  /* ⚠️ 本组件整体跑在 AutoFit（1920×1080 设计稿）缩放容器里：
     所有尺寸必须是固定设计像素 —— vw/vh 与视口媒体查询在缩放子树里
     指向的是真实视口，会算错；分辨率适配交给 AutoFit 的统一缩放。 */

  position: absolute;
  inset: 0;
  z-index: 25;
  pointer-events: none;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
  color: var(--txt);
  animation: ck-fade 0.45s ease-out backwards;
}

/* ============================ 顶栏 ============================ */
.ck-top {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 78px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0 22px 0 20px;
  pointer-events: auto;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(5, 12, 26, 0.96) 0%, rgba(6, 15, 32, 0.68) 70%, rgba(6, 15, 32, 0.18) 100%);
  border-bottom: 1px solid rgba(63, 216, 255, 0.24);
  animation: ck-drop 0.5s 0.05s cubic-bezier(0.22, 0.8, 0.28, 1) backwards;
}

.ck-top::after {
  content: '';
  position: absolute;
  left: 6%;
  right: 6%;
  bottom: -1px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(63, 216, 255, 0.7), transparent);
}

/* 横掠全宽的扫描光带，让顶栏"活"起来（纯 transform，不触发重排） */
.ck-beam {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 260px;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(63, 216, 255, 0.16), rgba(63, 216, 255, 0.04) 70%, transparent);
  animation: ck-beam 8s linear infinite;
}

.ck-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.ck-logo {
  position: relative;
  width: 30px;
  height: 34px;
  flex: none;
  clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
  background: linear-gradient(160deg, #3fd8ff, #2f7bff 60%, #1b3f8a);
  box-shadow: 0 0 18px rgba(63, 216, 255, 0.45);
  animation: ck-pulse 3.6s ease-in-out infinite;
}

.ck-logo::after {
  content: '';
  position: absolute;
  inset: 6px;
  clip-path: polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%);
  background: rgba(5, 12, 26, 0.92);
}

.ck-brand-text {
  min-width: 0;
}

.ck-brand-text h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2px;
  line-height: 1.2;
  white-space: nowrap;
  text-shadow: 0 0 22px rgba(63, 216, 255, 0.28);
}

.ck-brand-text h1 em {
  font-style: normal;
  color: var(--cyan);
  margin: 0 2px;
}

.ck-brand-text p {
  margin: 3px 0 0;
  font-size: 9px;
  letter-spacing: 2.4px;
  color: var(--mut);
}

/* 时钟 + 日期（原底栏，上移顶栏中部）：竖分隔线隔开品牌区 */
.ck-head-time {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: 38px;
  padding-left: 30px;
  border-left: 1px solid rgba(64, 150, 255, 0.22);
  flex: none;
}

.ck-clock {
  font-family: var(--font-num);
  font-size: 33px;
  font-weight: 800;
  letter-spacing: 2px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: #fff;
  text-shadow: 0 0 18px rgba(63, 216, 255, 0.5);
}

.ck-date {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.ck-date b {
  font-family: var(--font-num);
  font-size: 13px;
  font-weight: 600;
  color: var(--txt);
  font-variant-numeric: tabular-nums;
}

.ck-date span {
  font-size: 11px;
  color: var(--mut);
}

.ck-head-right {
  display: flex;
  align-items: center;
  gap: 30px;
  flex: none;
  margin-left: auto;
}

.ck-kpi {
  position: relative;
  text-align: right;
  padding-bottom: 4px;
}

/* 每个 KPI 底部一根会在 hover 时亮起的强调线 */
.ck-kpi::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(63, 216, 255, 0.6), transparent);
  opacity: 0.3;
  transition: opacity 0.25s ease, box-shadow 0.25s ease;
}

.ck-kpi:hover::after {
  opacity: 1;
  box-shadow: 0 0 10px rgba(63, 216, 255, 0.6);
}

.ck-kpi-label {
  display: block;
  font-size: 10px;
  letter-spacing: 1.2px;
  color: var(--mut);
}

.ck-live {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-right: 5px;
  border-radius: 50%;
  vertical-align: 1px;
  background: var(--green);
  box-shadow: 0 0 8px var(--green);
  animation: ck-pulse 1.6s ease-in-out infinite;
}

.ck-kpi-value {
  display: block;
  margin-top: 2px;
  font-family: var(--font-num);
  font-size: 24px;
  line-height: 1.15;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #fff;
  text-shadow: 0 0 16px rgba(63, 216, 255, 0.35);
}

.ck-kpi-value i {
  font-style: normal;
  font-size: 11px;
  font-weight: 600;
  margin-left: 3px;
  color: var(--cyan);
}

.ck-fold {
  height: 28px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid var(--line);
  background: rgba(63, 216, 255, 0.07);
  color: var(--txt-2);
  font-size: 11.5px;
  letter-spacing: 0.6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ck-fold:hover {
  border-color: var(--line-2);
  color: var(--cyan);
  background: rgba(63, 216, 255, 0.14);
  box-shadow: 0 0 14px rgba(63, 216, 255, 0.28);
}

/* 顶栏收起后唯一的展开入口，钉在顶栏按钮原来的位置上 */
.ck-fold--restore {
  position: absolute;
  top: 16px;
  right: 20px;
  z-index: 3;
  pointer-events: auto;
}

/* ============================ 两列卡片 ============================
   列体 top/bottom 锚定：顶栏 78px + 16px 间距，底栏 44px + 12px 间距，
   中间整段高度全部交给卡片列（占满全屏的关键就在这两个 absolute 值）。 */
.ck-col {
  position: absolute;
  top: 94px;
  bottom: 56px;
  width: var(--col-w);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  overflow: hidden auto;
  pointer-events: auto;
}

.ck-col--left {
  left: 12px;
  animation: ck-slide-l 0.5s 0.12s cubic-bezier(0.22, 0.8, 0.28, 1) backwards;
}

.ck-col--right {
  right: 12px;
  animation: ck-slide-r 0.5s 0.16s cubic-bezier(0.22, 0.8, 0.28, 1) backwards;
}

/* ---- 列骨架垂线：与列同跨度、贴列外缘，让两列卡片像挂件一样串在一根梁上 ----
   放在 .ck 根级而不是 .ck-col 伪元素上：列有 overflow:hidden，负偏移会被裁。 */
.ck-spine {
  position: absolute;
  top: 94px;
  bottom: 56px;
  width: 1px;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(180deg, transparent, rgba(63, 216, 255, 0.5) 5%, rgba(63, 216, 255, 0.5) 95%, transparent);
}

.ck-spine--l {
  left: 5px;
}

.ck-spine--r {
  right: 5px;
}

/* 沿线循环下滑的流光 */
.ck-spine::after {
  content: '';
  position: absolute;
  left: -0.5px;
  top: 0;
  width: 2px;
  height: 64px;
  border-radius: 1px;
  background: linear-gradient(180deg, transparent, rgba(130, 232, 255, 0.95), transparent);
  filter: drop-shadow(0 0 6px rgba(63, 216, 255, 0.8));
  animation: ck-spine-run 6s linear infinite;
}

.ck-spine--r::after {
  animation-delay: -3s;
}

/* 自定义滚动条（列 + 索引列表统一语言）：细青色圆角。
   ⚠️ 不写 scrollbar-width/scrollbar-color —— Chromium 121+ 里这两个标准属性
      一旦设置就会忽略 ::-webkit-scrollbar，退回原生 thin 样式（截图里那条粗亮条）。 */
.ck-col::-webkit-scrollbar {
  width: 6px;
}

.ck-col::-webkit-scrollbar-track {
  background: rgba(64, 150, 255, 0.06);
  border-radius: 3px;
}

.ck-col::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(63, 216, 255, 0.55), rgba(47, 123, 255, 0.4));
  border-radius: 3px;
}

.ck-col::-webkit-scrollbar-thumb:hover {
  background: rgba(63, 216, 255, 0.8);
}

/* 让卡片列纵向吃满：左列"建筑索引"弹性伸展（可见行数变多），剩余高度不再空在列尾。
   左列末张"实时客流趋势"卡固定 var(--dock-h)：与中部"街区经营监测"、
   右列"系统运行状态"构成等高底部条带，三卡顶部同线、底部同贴底栏上沿。 */
.ck-col--left > .ck-card:nth-child(2) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ck-col--left > .ck-card:nth-child(2) .ck-pad--tight {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ck-col--left > .ck-card:nth-child(2) .ck-list {
  flex: 1;
  max-height: none;
}

.ck-col--left > .ck-card:nth-child(3) {
  flex: none;
  height: var(--dock-h);
  display: flex;
  flex-direction: column;
}

.ck-col--left > .ck-card:nth-child(3) .ck-pad {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* 面积图随条带高度伸展（替代原固定 96px），轴刻度贴卡底 */
.ck-col--left > .ck-card:nth-child(3) .ck-flow-wrap {
  flex: 1;
  min-height: 0;
}

.ck-col--left > .ck-card:nth-child(3) .ck-flow-axis {
  flex: none;
}

/* 右列弹性给"实时事件预警"卡（列表 + 贴底的主力业态摘要，拉伸自然）；
   "业态结构分布"保持自然紧凑高度 —— 给它 flex:1 的话，方屏下 autofit 把设计面
   撑得很高，这张卡会被拉出大片的居中空白（实测踩过）。
   末张"系统运行状态"同样锁定 var(--dock-h) 参与底部条带对齐。 */
.ck-col--right > .ck-card:first-child {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ck-col--right > .ck-card:first-child .ck-pad {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ck-col--right > .ck-card:nth-child(5) {
  flex: none;
  height: var(--dock-h);
  display: flex;
  flex-direction: column;
}

.ck-col--right > .ck-card:nth-child(5) .ck-pad {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.ck-col--right > .ck-card:nth-child(5) .ck-sys {
  flex: 1;
  align-content: space-evenly;
}

.ck-col--right > .ck-card:first-child .ck-scope-biz {
  margin-top: auto;
}

.ck-card {
  position: relative;
  flex: none;
  box-sizing: border-box;
  border: 1px solid var(--line);
  border-radius: var(--r);
  /* 网格纹理压在半透明面板上，做出 HUD 底纹 */
  background: var(--grid), var(--panel);
  backdrop-filter: blur(14px) saturate(1.15);
  -webkit-backdrop-filter: blur(14px) saturate(1.15);
  box-shadow: 0 14px 34px rgba(2, 7, 16, 0.42), inset 0 1px 0 rgba(174, 214, 255, 0.08);
  overflow: hidden;
  transition: transform 0.28s cubic-bezier(0.22, 0.8, 0.28, 1), border-color 0.28s ease,
    box-shadow 0.28s ease;
}

/* 卡片顶部一道短辉光，像仪表盘的指示灯带 */
.ck-card::before {
  content: '';
  position: absolute;
  left: 10px;
  right: 10px;
  top: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(63, 216, 255, 0.55), transparent);
  opacity: 0.55;
  pointer-events: none;
}

.ck-card:hover {
  transform: translateY(-2px);
  border-color: rgba(63, 216, 255, 0.42);
  box-shadow: 0 20px 44px rgba(2, 7, 16, 0.55), 0 0 0 1px rgba(63, 216, 255, 0.08),
    inset 0 1px 0 rgba(174, 214, 255, 0.16);
}

/* 带洞的那张：底色拆到上下两条 .ck-band，容器只留描边当窗框。
   ⚠️ 绝不能做位移/缩放 —— 里面的三维窗口靠 getBoundingClientRect 对位，
      卡片一动，scissor 视口就会跟着抖。 */
.ck-card--cut {
  background: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow: none;
  transition: border-color 0.28s ease;
}

.ck-card--cut::before {
  display: none;
}

.ck-card--cut:hover {
  transform: none;
  box-shadow: none;
  border-color: rgba(63, 216, 255, 0.42);
}

.ck-band {
  background: var(--grid), var(--panel);
  backdrop-filter: blur(14px) saturate(1.15);
  -webkit-backdrop-filter: blur(14px) saturate(1.15);
}

.ck-t {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px 8px;
  border-bottom: 1px solid rgba(64, 150, 255, 0.16);
}

.ck-t h2 {
  position: relative;
  margin: 0;
  padding-left: 10px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  white-space: nowrap;
}

.ck-t h2::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 2px;
  width: 3px;
  border-radius: 2px;
  background: linear-gradient(180deg, var(--cyan), rgba(63, 216, 255, 0.25));
  box-shadow: 0 0 8px rgba(63, 216, 255, 0.6);
}

/* 卡头右上角的 HUD 括号：与详情窗口四角括号同一语言，强化"同一套面板"的系列感 */
.ck-t::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  width: 9px;
  height: 9px;
  border-top: 1.5px solid rgba(63, 216, 255, 0.6);
  border-right: 1.5px solid rgba(63, 216, 255, 0.6);
  border-top-right-radius: 4px;
  pointer-events: none;
}

.ck-note {
  margin-left: auto;
  font-size: 11px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.ck-note.is-ok {
  color: var(--green);
}

.ck-note.is-warn {
  color: var(--amber);
}

.ck-chev {
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  border: 1px solid var(--line);
  background: transparent;
  color: var(--mut);
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ck-chev:hover {
  color: var(--cyan);
  border-color: var(--line-2);
  background: rgba(63, 216, 255, 0.12);
}

.ck-pad {
  padding: 10px 12px 12px;
}

/* 模拟数据角标：和真数据一眼分开 */
.ck-sim {
  font-style: normal;
  display: inline-block;
  margin-left: 3px;
  padding: 0 3px;
  border-radius: 3px;
  background: rgba(245, 166, 35, 0.16);
  border: 1px solid rgba(245, 166, 35, 0.45);
  color: var(--amber);
  font-size: 9px;
  line-height: 1.45;
  letter-spacing: 0.3px;
  vertical-align: 1px;
}

/* ---- 2x2 指标格 ---- */
.ck-mgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.ck-metric {
  position: relative;
  overflow: hidden;
  padding: 5px 9px 9px;
  border-radius: 6px;
  border: 1px solid rgba(64, 150, 255, 0.16);
  background: rgba(63, 216, 255, 0.05);
  transition: border-color 0.22s ease, background 0.22s ease;
}

.ck-metric:hover {
  border-color: rgba(63, 216, 255, 0.4);
  background: rgba(63, 216, 255, 0.1);
}

.ck-metric span {
  display: block;
  font-size: 10.5px;
  letter-spacing: 0.5px;
  color: var(--mut);
}

.ck-metric b {
  display: block;
  margin-top: 1px;
  font-family: var(--font-num);
  font-size: 18px;
  line-height: 1.15;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #fff;
}

.ck-metric i {
  font-style: normal;
  font-size: 10.5px;
  font-weight: 600;
  margin-left: 3px;
  color: var(--cyan);
}

/* 指标格底部的能量微条 */
.ck-mbar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  height: 3px;
  background: rgba(64, 150, 255, 0.14);
}

.ck-mbar > i {
  display: block;
  height: 100%;
  margin: 0;
  border-radius: 0 2px 2px 0;
  transition: width 0.9s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.tone-temp .ck-mbar > i {
  background: linear-gradient(90deg, #ff7a3d, #ffd06b);
}

.tone-hum .ck-mbar > i {
  background: linear-gradient(90deg, #2f7bff, #3fd8ff);
}

.tone-pow .ck-mbar > i {
  background: linear-gradient(90deg, #8b7bff, #b7a6ff);
}

.tone-wat .ck-mbar > i {
  background: linear-gradient(90deg, #14b877, #2ee6a6);
}

/* ---- 建筑详情卡的正文 ---- */
.ck-intro {
  margin: 9px 0 0;
  font-size: 11.5px;
  line-height: 1.75;
  color: var(--txt-2);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ck-inline {
  margin: 8px 0 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  font-size: 10.5px;
}

.ck-inline > div {
  display: flex;
  gap: 5px;
  min-width: 0;
}

.ck-inline dt {
  color: var(--mut);
  flex: none;
}

.ck-inline dd {
  margin: 0;
  color: #dfeaf9;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* ---- 建筑索引 ---- */
.ck-pad--tight {
  padding: 9px 12px 10px;
}

.ck-search input {
  width: 100%;
  box-sizing: border-box;
  height: 28px;
  padding: 0 10px;
  border-radius: 5px;
  border: 1px solid var(--line);
  background: rgba(4, 11, 24, 0.6);
  color: var(--txt);
  font-size: 11.5px;
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.ck-search input::placeholder {
  color: rgba(127, 151, 184, 0.8);
}

.ck-search input:focus {
  border-color: rgba(63, 216, 255, 0.55);
  box-shadow: 0 0 0 2px rgba(63, 216, 255, 0.1);
}

.ck-list {
  margin: 8px 0 0;
  padding: 0 2px 0 0;
  list-style: none;
  max-height: 132px;
  overflow: hidden auto;
}

/* 与列滚动条同一套自定义语言 */
.ck-list::-webkit-scrollbar {
  width: 5px;
}

.ck-list::-webkit-scrollbar-track {
  background: rgba(64, 150, 255, 0.06);
  border-radius: 3px;
}

.ck-list::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, rgba(63, 216, 255, 0.55), rgba(47, 123, 255, 0.4));
  border-radius: 3px;
}

.ck-list::-webkit-scrollbar-thumb:hover {
  background: rgba(63, 216, 255, 0.8);
}

.ck-list li {
  position: relative;
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: center;
  gap: 8px;
  padding: 5px 6px 6px;
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.16s ease, transform 0.16s ease;
}

.ck-list li:hover {
  background: rgba(63, 216, 255, 0.08);
  transform: translateX(2px);
}

.ck-list li.is-active {
  background: linear-gradient(90deg, rgba(63, 216, 255, 0.2), rgba(63, 216, 255, 0.02));
  box-shadow: inset 0 0 0 1px rgba(63, 216, 255, 0.34);
}

.ck-list-code {
  display: grid;
  place-items: center;
  height: 20px;
  border-radius: 4px;
  background: rgba(63, 216, 255, 0.12);
  color: var(--cyan);
  font-family: var(--font-num);
  font-size: 11px;
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  transition: all 0.2s ease;
}

.ck-list li.is-active .ck-list-code {
  background: var(--cyan);
  color: #04101f;
  box-shadow: 0 0 12px rgba(63, 216, 255, 0.55);
}

.ck-list-name {
  font-size: 12px;
  color: var(--txt-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ck-list-num {
  font-family: var(--font-num);
  font-size: 11px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
}

/* 行底商户数对比条：宽度过渡 + 流光扫过 */
.ck-list-bar {
  position: absolute;
  left: 6px;
  bottom: 2px;
  height: 2px;
  border-radius: 2px;
  overflow: hidden;
  background: linear-gradient(90deg, rgba(63, 216, 255, 0.85), rgba(47, 123, 255, 0.15));
  transition: width 0.7s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.ck-list-bar::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.85), transparent);
  transform: translateX(-100%);
  animation: ck-sheen 2.6s ease-in-out infinite;
}

.ck-list-empty {
  display: block;
  padding: 18px 8px;
  color: var(--mut);
  font-size: 12px;
  text-align: center;
}

/* ---- 建筑详情正文：左边四格指标，右边入驻率环 ---- */
.ck-detail-body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.ck-detail-body .ck-mgrid {
  flex: 1;
  min-width: 0;
}

.ck-rent {
  flex: none;
  width: 86px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.ck-ring {
  width: 74px;
  height: 74px;
  filter: drop-shadow(0 0 6px rgba(63, 216, 255, 0.3));
}

.ck-ring-track {
  fill: none;
  stroke-width: 9;
}

.ck-ring-arc {
  fill: none;
  stroke-width: 9;
  stroke-linecap: round;
  transition: stroke-dasharray 0.9s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.ck-ring-num {
  font-family: var(--font-num);
  font-size: 16px;
  font-weight: 800;
  fill: #fff;
}

.ck-ring-sub {
  font-size: 7px;
  fill: var(--mut);
  letter-spacing: 0.6px;
}

.ck-legend-v {
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
}

.ck-legend-v li {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: var(--txt-2);
  line-height: 1.6;
}

.ck-legend-v i {
  width: 6px;
  height: 6px;
  border-radius: 1px;
  flex: none;
}

.ck-legend-v b {
  margin-left: auto;
  font-family: var(--font-num);
  color: #fff;
  font-variant-numeric: tabular-nums;
}

/* ============================ 分时客流面积图 ============================ */
.ck-flow-wrap {
  position: relative;
  height: 96px;
}

.ck-flow-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: repeating-linear-gradient(to bottom, rgba(64, 150, 255, 0.13) 0 1px, transparent 1px 25%);
}

.ck-flow {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.ck-flow-area {
  animation: ck-fade 0.9s 0.25s ease-out backwards;
}

/* pathLength=100 把路径长度归一化，dashoffset 从 100 收到 0 即为"画线"效果 */
.ck-flow-line {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  animation: ck-draw 1.3s 0.15s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  filter: drop-shadow(0 0 5px rgba(63, 216, 255, 0.6));
}

/* "现在"这根虚线指针 */
.ck-flow-now {
  stroke: rgba(63, 216, 255, 0.75);
  stroke-width: 1;
  stroke-dasharray: 3 4;
  filter: drop-shadow(0 0 4px rgba(63, 216, 255, 0.6));
}

.ck-flow-dots {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.ck-flow-dot {
  position: absolute;
  /* border 计入 6px 内：content-box 会让实际占 9px、margin:-3px 补偿不足，
     点中心整体偏右下 1.5px —— 在 96px 高的图上就是肉眼可见的"点不在线上" */
  box-sizing: border-box;
  width: 6px;
  height: 6px;
  margin: -3px 0 0 -3px;
  border-radius: 50%;
  background: #0a1730;
  border: 1.5px solid rgba(63, 216, 255, 0.75);
  transition: transform 0.18s ease, background 0.18s ease;
  animation: ck-pop 0.4s ease-out backwards;
}

.ck-flow-dot.is-now {
  background: var(--amber);
  border-color: #ffd47a;
  box-shadow: 0 0 0 0 rgba(245, 166, 35, 0.55);
  animation: ck-ripple 2s ease-out infinite;
}

.ck-flow-dot.is-on {
  background: #fff;
  border-color: #fff;
  transform: scale(1.7);
  box-shadow: 0 0 12px rgba(63, 216, 255, 0.9);
}

.ck-flow-zones {
  position: absolute;
  inset: 0;
  display: flex;
}

.ck-flow-zone {
  flex: 1;
  position: relative;
  cursor: crosshair;
}

.ck-flow-zone.is-on {
  background: linear-gradient(180deg, rgba(63, 216, 255, 0.16), transparent);
}

.ck-flow-zone.is-on::after {
  content: '';
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(63, 216, 255, 0.7);
  box-shadow: 0 0 8px rgba(63, 216, 255, 0.7);
}

.ck-flow-tip {
  position: absolute;
  top: 0;
  z-index: 3;
  transform: translateX(-50%);
  padding: 3px 8px;
  border-radius: 5px;
  white-space: nowrap;
  pointer-events: none;
  background: rgba(6, 20, 40, 0.94);
  border: 1px solid rgba(63, 216, 255, 0.5);
  box-shadow: 0 6px 18px rgba(2, 7, 16, 0.6), 0 0 14px rgba(63, 216, 255, 0.2);
}

.ck-flow-tip b {
  font-family: var(--font-num);
  font-size: 12.5px;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.ck-flow-tip span {
  display: block;
  font-size: 9.5px;
  color: var(--mut);
  letter-spacing: 0.4px;
}

.ck-flow-axis {
  display: flex;
  margin-top: 4px;
}

.ck-flow-axis span {
  flex: 1;
  text-align: center;
  font-family: var(--font-num);
  font-size: 9px;
  letter-spacing: 0.5px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
  transition: color 0.2s ease;
}

.ck-flow-axis span.is-on {
  color: var(--cyan);
}

/* ============================ 事件预警 ============================ */
.ck-chips {
  display: flex;
  gap: 6px;
  margin-bottom: 9px;
}

.ck-chip {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 11px;
  color: var(--txt-2);
  background: rgba(63, 216, 255, 0.05);
  border: 1px solid rgba(64, 150, 255, 0.16);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.ck-chip:hover {
  background: rgba(63, 216, 255, 0.12);
  border-color: rgba(63, 216, 255, 0.4);
}

.ck-chip b {
  margin-left: auto;
  font-family: var(--font-num);
  font-size: 14px;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.ck-events {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 7px;
}

.ck-events li {
  display: flex;
  align-items: center;
  gap: 7px;
  padding-left: 7px;
  border-left: 2px solid rgba(63, 216, 255, 0.5);
  font-size: 11.5px;
  color: var(--txt-2);
  min-width: 0;
  animation: ck-row-in 0.45s cubic-bezier(0.22, 0.8, 0.28, 1) backwards;
  transition: background 0.18s ease;
}

.ck-events li:hover {
  background: rgba(63, 216, 255, 0.06);
}

.ck-events li.lv-high {
  border-left-color: var(--red);
}

.ck-events li.lv-mid {
  border-left-color: var(--amber);
}

.ck-events li.lv-low {
  border-left-color: var(--green);
}

.ck-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex: none;
  background: var(--cyan);
  box-shadow: 0 0 8px currentColor;
  color: var(--cyan);
}

.lv-high .ck-dot {
  background: var(--red);
  color: var(--red);
  animation: ck-pulse 1.8s ease-in-out infinite;
}

.lv-mid .ck-dot {
  background: var(--amber);
  color: var(--amber);
}

.lv-low .ck-dot {
  background: var(--green);
  color: var(--green);
}

.ck-ev-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ck-ev-time {
  margin-left: auto;
  font-family: var(--font-num);
  color: var(--mut);
  font-weight: 400;
  font-variant-numeric: tabular-nums;
  flex: none;
}

.ck-ev-state {
  font-style: normal;
  flex: none;
  color: var(--cyan);
}

.lv-high .ck-ev-state {
  color: var(--red);
}

.lv-mid .ck-ev-state {
  color: var(--amber);
}

.lv-low .ck-ev-state {
  color: var(--green);
}

/* ============================ 业态结构环形图 ============================ */
.ck-scope {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ck-donut-wrap {
  position: relative;
  flex: none;
  width: 104px;
  height: 104px;
}

.ck-donut {
  width: 100%;
  height: 100%;
  transform: rotate(0deg);
}

.ck-donut-track {
  fill: none;
  stroke: rgba(64, 150, 255, 0.14);
  stroke-width: 14;
}

.ck-donut-seg {
  fill: none;
  stroke-width: 14;
  cursor: pointer;
  pointer-events: stroke;
  transition: stroke-width 0.25s ease, opacity 0.25s ease, filter 0.25s ease;
}

.ck-donut-seg.is-on {
  stroke-width: 18;
  filter: drop-shadow(0 0 6px currentColor);
}

.ck-donut-seg.is-dim {
  opacity: 0.25;
}

.ck-donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.ck-donut-center b {
  font-family: var(--font-num);
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 14px rgba(63, 216, 255, 0.35);
}

.ck-donut-center span {
  font-size: 9.5px;
  letter-spacing: 0.6px;
  color: var(--mut);
}

.ck-scope-side {
  flex: 1;
  min-width: 0;
}

/* 主力业态摘要行（在"实时事件预警"卡底部）：占比前三 + 色点 */
.ck-scope-biz {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 3px 12px;
  margin-top: 9px;
  padding: 7px 9px 6px;
  border-radius: 6px;
  border: 1px solid rgba(64, 150, 255, 0.16);
  background: rgba(63, 216, 255, 0.05);
  font-size: 10.5px;
  color: var(--txt-2);
}

.ck-scope-biz-label {
  color: var(--mut);
  letter-spacing: 0.5px;
  flex: none;
}

.ck-scope-biz-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.ck-scope-biz-item i {
  width: 7px;
  height: 7px;
  border-radius: 2px;
  flex: none;
}

.ck-scope-biz-item b {
  font-family: var(--font-num);
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.ck-legend {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3px 10px;
}

.ck-legend li {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 11.5px;
  color: var(--txt-2);
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;
}

.ck-legend li.is-on {
  background: rgba(63, 216, 255, 0.12);
  transform: translateX(2px);
}

.ck-legend i {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex: none;
}

.ck-legend span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ck-legend b {
  margin-left: auto;
  font-family: var(--font-num);
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.ck-legend-empty {
  color: var(--mut);
  font-size: 11.5px;
  cursor: default;
}

/* ============================ 负载进度 ============================ */
.ck-load {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 7px;
}

.ck-load li {
  display: grid;
  grid-template-columns: 1fr 96px 34px;
  align-items: center;
  gap: 9px;
  font-size: 11.5px;
  color: var(--txt-2);
}

.ck-load span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ck-track {
  display: block;
  height: 6px;
  border-radius: 3px;
  background: rgba(64, 150, 255, 0.16);
  overflow: hidden;
}

.ck-fill {
  position: relative;
  display: block;
  height: 100%;
  border-radius: 3px;
  background: linear-gradient(90deg, #2f7bff, #3fd8ff);
  box-shadow: 0 0 10px rgba(63, 216, 255, 0.4);
  transition: width 0.7s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.ck-fill::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent);
  transform: translateX(-100%);
  animation: ck-sheen 2.4s ease-in-out infinite;
}

.ck-fill.is-hot {
  background: linear-gradient(90deg, #f5a623, #ffd47a);
  box-shadow: 0 0 10px rgba(245, 166, 35, 0.45);
}

.ck-load b {
  text-align: right;
  font-family: var(--font-num);
  color: #fff;
  font-variant-numeric: tabular-nums;
}

/* ============================ 系统状态 ============================ */
.ck-sys {
  margin: 0;
  padding: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.ck-sys li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid rgba(64, 150, 255, 0.16);
  background: rgba(63, 216, 255, 0.04);
  transition: border-color 0.2s ease, background 0.2s ease;
}

.ck-sys li:hover {
  border-color: rgba(63, 216, 255, 0.4);
  background: rgba(63, 216, 255, 0.1);
}

.ck-sys-ring {
  position: relative;
  flex: none;
  width: 34px;
  height: 34px;
}

.ck-sys-ring svg {
  width: 100%;
  height: 100%;
  display: block;
}

.ck-sys-ring .ck-ring-track {
  stroke: rgba(64, 150, 255, 0.18);
  stroke-width: 4;
}

.ck-sys-ring .ck-ring-arc {
  stroke: var(--cyan);
  stroke-width: 4;
  filter: drop-shadow(0 0 4px rgba(63, 216, 255, 0.6));
}

.tone-warn .ck-sys-ring .ck-ring-arc {
  stroke: var(--amber);
  filter: drop-shadow(0 0 4px rgba(245, 166, 35, 0.6));
}

.ck-sys-ring b {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  font-family: var(--font-num);
  font-size: 8.5px;
  color: var(--txt);
  font-variant-numeric: tabular-nums;
}

.ck-sys-meta {
  flex: 1;
  min-width: 0;
}

.ck-sys-label {
  display: block;
  font-size: 10.5px;
  color: var(--mut);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ck-sys-value {
  display: block;
  font-family: var(--font-num);
  font-size: 15px;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

.ck-sys-tag {
  flex: none;
  font-style: normal;
  font-size: 9.5px;
  color: var(--cyan);
}

.tone-warn .ck-sys-tag {
  color: var(--amber);
}

/* ============================ 图层开关（竖排） ============================ */
.ck-layers {
  position: absolute;
  left: calc(12px + var(--col-w) + 12px);
  top: 116px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  pointer-events: auto;
  z-index: 2;
}

.ck-layers button {
  width: 46px;
  height: 34px;
  border-radius: 6px;
  border: 1px solid var(--line);
  background: rgba(6, 15, 32, 0.82);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: var(--txt-2);
  font-size: 11.5px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ck-layers button:hover:not(:disabled) {
  border-color: var(--line-2);
  color: var(--cyan);
  transform: translateX(3px);
  box-shadow: 0 0 14px rgba(63, 216, 255, 0.25);
}

.ck-layers button.is-on {
  border-color: rgba(63, 216, 255, 0.6);
  background: rgba(63, 216, 255, 0.16);
  color: #eefaff;
  box-shadow: 0 0 14px rgba(63, 216, 255, 0.3), inset 0 0 10px rgba(63, 216, 255, 0.12);
}

.ck-layers button:disabled {
  cursor: default;
}

/* ============================ 底部经营监测面板 ============================
   横跨左右两列之间、贴底栏上方，高度锁定 var(--dock-h)：
   与左"实时客流趋势"、右"系统运行状态"构成等高底部条带。 */
.ck-biz-panel {
  position: absolute;
  left: calc(12px + var(--col-w) + 12px);
  right: calc(12px + var(--col-w) + 12px);
  bottom: 56px;
  height: var(--dock-h);
  pointer-events: auto;
  z-index: 2;
  animation: ck-rise 0.5s 0.24s cubic-bezier(0.22, 0.8, 0.28, 1) backwards;
}

.ck-biz-cols {
  display: flex;
  align-items: stretch;
  height: calc(100% - 42px);
  padding: 4px 14px 12px;
  box-sizing: border-box;
}

.ck-biz-col {
  flex: 1;
  min-width: 0;
  padding: 2px 16px 0;
  display: flex;
  flex-direction: column;
}

.ck-biz-col + .ck-biz-col {
  border-left: 1px solid rgba(64, 150, 255, 0.14);
}

.ck-biz-col--bars {
  flex: 1.25;
}

.ck-biz-col--park {
  flex: 0.8;
  align-items: center;
}

.ck-biz-title {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 11.5px;
  color: var(--txt-2);
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  white-space: nowrap;
}

.ck-biz-unit {
  font-size: 9.5px;
  color: var(--mut);
  letter-spacing: 0;
}

/* ---- 近 7 日客流柱图 ---- */
.ck-bars7 {
  flex: 1;
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.ck-bar7 {
  position: relative;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.ck-bar7-val {
  font-style: normal;
  font-family: var(--font-num);
  font-size: 10px;
  color: var(--mut);
  font-variant-numeric: tabular-nums;
  margin-bottom: 3px;
  opacity: 0;
  transform: translateY(3px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.ck-bar7:hover .ck-bar7-val,
.ck-bar7.is-today .ck-bar7-val {
  opacity: 1;
  transform: none;
}

.ck-bar7.is-today .ck-bar7-val {
  color: var(--amber);
}

.ck-bar7-pill {
  width: 100%;
  max-width: 26px;
  border-radius: 3px 3px 0 0;
  background: linear-gradient(180deg, #4fd8ff, rgba(47, 123, 255, 0.3));
  box-shadow: 0 0 10px rgba(63, 216, 255, 0.25);
  transition: height 0.7s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.ck-bar7.is-today .ck-bar7-pill {
  background: linear-gradient(180deg, #ffd47a, rgba(245, 166, 35, 0.4));
  box-shadow: 0 0 12px rgba(245, 166, 35, 0.4);
}

.ck-bar7 span {
  margin-top: 4px;
  font-size: 9.5px;
  color: var(--mut);
}

/* ---- 品类销售排行 ---- */
.ck-rank5 {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ck-rank5 li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: var(--txt-2);
  min-width: 0;
}

.ck-rank-idx {
  width: 15px;
  height: 15px;
  border-radius: 4px;
  display: grid;
  place-items: center;
  flex: none;
  font-family: var(--font-num);
  font-size: 10px;
  font-style: normal;
  color: var(--mut);
  background: rgba(64, 150, 255, 0.12);
}

.ck-rank-idx.is-top {
  background: rgba(63, 216, 255, 0.9);
  color: #04101f;
  box-shadow: 0 0 10px rgba(63, 216, 255, 0.5);
}

.ck-rank-name {
  width: 56px;
  flex: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ck-rank-track {
  flex: 1;
  height: 5px;
  border-radius: 3px;
  background: rgba(64, 150, 255, 0.14);
  overflow: hidden;
}

.ck-rank-track i {
  display: block;
  height: 100%;
  border-radius: 3px;
  opacity: 0.85;
  transition: width 0.7s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.ck-rank-num {
  flex: none;
  width: 36px;
  text-align: right;
  font-family: var(--font-num);
  font-size: 11.5px;
  color: #fff;
  font-variant-numeric: tabular-nums;
}

/* ---- 智慧停车占用环 ---- */
.ck-park {
  position: relative;
  width: 86px;
  height: 86px;
  margin-top: 2px;
}

.ck-park svg {
  width: 100%;
  height: 100%;
  display: block;
}

.ck-park-track {
  fill: none;
  stroke: rgba(64, 150, 255, 0.16);
  stroke-width: 8;
}

.ck-park-arc {
  fill: none;
  stroke: var(--cyan);
  stroke-width: 8;
  stroke-linecap: round;
  filter: drop-shadow(0 0 5px rgba(63, 216, 255, 0.55));
  transition: stroke-dasharray 0.8s cubic-bezier(0.22, 0.8, 0.28, 1);
}

.ck-park-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  pointer-events: none;
}

.ck-park-center b {
  font-family: var(--font-num);
  font-size: 16px;
  font-weight: 800;
  color: #fff;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 0 12px rgba(63, 216, 255, 0.35);
}

.ck-park-center span {
  font-size: 8.5px;
  letter-spacing: 0.5px;
  color: var(--mut);
}

.ck-park-meta {
  display: flex;
  gap: 14px;
  margin: 4px 0 0;
  font-size: 10.5px;
}

.ck-park-meta div {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.ck-park-meta dt {
  color: var(--mut);
}

.ck-park-meta dd {
  margin: 0;
  font-family: var(--font-num);
  color: #fff;
  font-variant-numeric: tabular-nums;
}

/* ============================ 底栏（视角读数 + 口径） ============================ */
.ck-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 44px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 0 20px;
  pointer-events: auto;
  background: linear-gradient(0deg, rgba(5, 12, 26, 0.96) 0%, rgba(6, 15, 32, 0.5) 100%);
  border-top: 1px solid rgba(63, 216, 255, 0.2);
  animation: ck-rise 0.5s 0.2s cubic-bezier(0.22, 0.8, 0.28, 1) backwards;
}

.ck-bottom::before {
  content: '';
  position: absolute;
  left: 6%;
  right: 6%;
  top: -1px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(63, 216, 255, 0.65), transparent);
}

.ck-legend-view {
  display: flex;
  align-items: center;
  gap: 18px;
  font-size: 12px;
  color: var(--mut);
  white-space: nowrap;
}

.ck-legend-view i {
  font-style: normal;
}

.ck-legend-view b {
  font-family: var(--font-num);
  color: var(--cyan);
  font-variant-numeric: tabular-nums;
  margin-left: 2px;
}

.ck-source {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  font-size: 11px;
  color: var(--mut);
  white-space: nowrap;
}

/* 底栏右下角的水印开关：与 .ck-chev 同高，点亮态沿用图层开关那套青色 */
.ck-wm {
  flex: none;
  height: 22px;
  padding: 0 9px;
  border-radius: 5px;
  border: 1px solid var(--line);
  background: transparent;
  color: var(--mut);
  font-family: inherit;
  font-size: 11px;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ck-wm:hover {
  color: var(--cyan);
  border-color: var(--line-2);
  background: rgba(63, 216, 255, 0.12);
}

.ck-wm.is-on {
  color: #eefaff;
  border-color: rgba(63, 216, 255, 0.6);
  background: rgba(63, 216, 255, 0.16);
  box-shadow: 0 0 12px rgba(63, 216, 255, 0.25), inset 0 0 8px rgba(63, 216, 255, 0.1);
}

/* ============================ 收起后的把手 ============================ */
.ck-tab {
  position: absolute;
  top: 50%;
  width: 30px;
  padding: 14px 0;
  transform: translateY(-50%);
  border: 1px solid var(--line);
  background: rgba(6, 15, 32, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: var(--txt-2);
  font-size: 11.5px;
  letter-spacing: 2px;
  writing-mode: vertical-rl;
  cursor: pointer;
  pointer-events: auto;
  z-index: 2;
  transition: all 0.22s ease;
}

.ck-tab:hover {
  color: var(--cyan);
  border-color: var(--line-2);
  box-shadow: 0 0 18px rgba(63, 216, 255, 0.25);
}

.ck-tab--left {
  left: 0;
  border-left: 0;
  border-radius: 0 8px 8px 0;
}

.ck-tab--right {
  right: 0;
  border-right: 0;
  border-radius: 8px 0 0 8px;
}

/* ============================ 三维窗口（洞） ============================ */
.ck-viewer {
  position: relative;
  height: 216px;
  /* margin: 0 12px; */
  /* 必须显式打开：.ck 根是 pointer-events:none，只有各块板自己恢复成 auto，
     而这个窗口恰恰是"故意不带背景"的镂空区，少这一句拖拽就穿到画布上，
     表现成"在模型上拖，转的却是整张地图"（实测踩过）。 */
  pointer-events: auto;
  background: transparent;
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
  touch-action: none;
}

.ck-viewer.is-orbiting {
  cursor: grabbing;
}

.ck-viewer::before {
  content: '';
  position: absolute;
  inset: 0;
  border: 1px solid rgba(63, 216, 255, 0.16);
  border-radius: 4px;
  background: radial-gradient(120% 92% at 50% 108%, rgba(63, 216, 255, 0.08), transparent 62%);
  pointer-events: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.ck-viewer:hover::before {
  border-color: rgba(63, 216, 255, 0.4);
  box-shadow: inset 0 0 26px rgba(63, 216, 255, 0.12);
}

.ck-viewer.is-orbiting::before {
  border-color: rgba(63, 216, 255, 0.55);
}

.ck-brk {
  position: absolute;
  width: 13px;
  height: 13px;
  border: 1.5px solid var(--cyan);
  pointer-events: none;
  animation: ck-pulse 3s ease-in-out infinite;
}

.ck-brk--tl {
  left: 0;
  top: 0;
  border-right: 0;
  border-bottom: 0;
}

.ck-brk--tr {
  right: 0;
  top: 0;
  border-left: 0;
  border-bottom: 0;
  animation-delay: 0.4s;
}

.ck-brk--bl {
  left: 0;
  bottom: 0;
  border-right: 0;
  border-top: 0;
  animation-delay: 0.8s;
}

.ck-brk--br {
  right: 0;
  bottom: 0;
  border-left: 0;
  border-top: 0;
  animation-delay: 1.2s;
}

.ck-viewer-tag {
  position: absolute;
  left: 10px;
  top: 9px;
  min-width: 26px;
  padding: 2px 9px;
  border-radius: 4px;
  background: rgba(63, 216, 255, 0.9);
  color: #04101f;
  font-family: var(--font-num);
  font-size: 12px;
  font-weight: 700;
  text-align: center;
  box-shadow: 0 0 16px rgba(63, 216, 255, 0.45);
}

.ck-viewer-hint {
  position: absolute;
  right: 10px;
  bottom: 9px;
  font-size: 10px;
  letter-spacing: 0.6px;
  color: rgba(145, 168, 201, 0.92);
}

/* ---- 未选中空态：这块"熄灭的屏"自带底色（仅在未选中时挂载，不会盖到模型），
     选中后整层随 v-if 移除，洞区恢复全透明给 scissor 用 ---- */
.ck-viewer-empty {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  pointer-events: none;
  background: radial-gradient(90% 120% at 50% 0%, rgba(63, 216, 255, 0.1), transparent 55%),
    repeating-linear-gradient(0deg, rgba(63, 216, 255, 0.045) 0 1px, transparent 1px 26px),
    repeating-linear-gradient(90deg, rgba(63, 216, 255, 0.045) 0 1px, transparent 1px 26px),
    linear-gradient(180deg, rgba(11, 26, 50, 0.78) 0%, rgba(6, 15, 32, 0.9) 100%);
  box-shadow: inset 0 0 30px rgba(2, 7, 16, 0.55);
  animation: ck-fade 0.3s ease-out backwards;
}

.ck-ve-ring {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 92px;
  height: 92px;
  margin: -46px 0 0 -46px;
  border: 1px dashed rgba(63, 216, 255, 0.45);
  border-radius: 50%;
  animation: ck-spin 16s linear infinite;
}

/* 环上的两颗轨道点，随环一起转 */
.ck-ve-ring::before,
.ck-ve-ring::after {
  content: '';
  position: absolute;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--cyan);
  box-shadow: 0 0 10px rgba(63, 216, 255, 0.9);
}

.ck-ve-ring::before {
  left: 50%;
  top: -3px;
  margin-left: -3px;
}

.ck-ve-ring::after {
  left: -3px;
  top: 50%;
  margin-top: -3px;
}

.ck-ve-cross {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 26px;
  height: 26px;
  margin: -13px 0 0 -13px;
}

.ck-ve-cross::before,
.ck-ve-cross::after {
  content: '';
  position: absolute;
  background: rgba(63, 216, 255, 0.65);
}

.ck-ve-cross::before {
  left: 50%;
  top: 0;
  bottom: 0;
  width: 1px;
  margin-left: -0.5px;
}

.ck-ve-cross::after {
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  margin-top: -0.5px;
}

.ck-viewer-empty b {
  margin-top: 66px;
  font-size: 12.5px;
  letter-spacing: 1px;
  color: var(--txt-2);
}

.ck-viewer-empty span {
  font-size: 10.5px;
  letter-spacing: 0.4px;
  color: var(--mut);
}

/* ---- 切换建筑：一道青色光带从上扫过洞区，正文轻微上滑淡入 ----
   ::after 平时 opacity:0 不遮模型；仅在 is-switching 的 0.72s 内扫一遍。 */
.ck-viewer::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  background: linear-gradient(180deg, transparent, rgba(63, 216, 255, 0.26) 44%, rgba(130, 232, 255, 0.55) 50%, rgba(63, 216, 255, 0.26) 56%, transparent);
  background-size: 100% 240%;
  background-position: 0 -120%;
}

.ck-viewer.is-switching::after {
  animation: ck-sweep 0.72s cubic-bezier(0.4, 0, 0.2, 1);
}

.ck-pad.is-switching {
  animation: ck-swap 0.55s cubic-bezier(0.22, 0.8, 0.28, 1);
}

/* ============================ 关键帧 ============================
   ⚠️ fill 只能用 backwards：both/forwards 会把末态 transform 永久留在元素上，
   之后收起态的位移就被它盖掉。 */
@keyframes ck-fade {
  from {
    opacity: 0;
  }
}

@keyframes ck-drop {
  from {
    transform: translateY(-100%);
  }
}

@keyframes ck-rise {
  from {
    transform: translateY(100%);
  }
}

@keyframes ck-slide-l {
  from {
    transform: translateX(calc(-100% - 14px));
    opacity: 0;
  }
}

@keyframes ck-slide-r {
  from {
    transform: translateX(calc(100% + 14px));
    opacity: 0;
  }
}

@keyframes ck-beam {
  /* 缩放子树里 vw 指向真实视口，会算错 —— 用固定设计像素扫过整个顶栏 */
  from {
    transform: translateX(-280px);
  }

  to {
    transform: translateX(2800px);
  }
}

@keyframes ck-draw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes ck-pop {
  from {
    opacity: 0;
    transform: scale(0.4);
  }
}

@keyframes ck-ripple {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 166, 35, 0.55);
  }

  70% {
    box-shadow: 0 0 0 9px rgba(245, 166, 35, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(245, 166, 35, 0);
  }
}

@keyframes ck-pulse {
  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.45;
  }
}

@keyframes ck-sheen {
  0% {
    transform: translateX(-100%);
  }

  55%,
  100% {
    transform: translateX(100%);
  }
}

@keyframes ck-row-in {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
}

@keyframes ck-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes ck-sweep {
  0% {
    opacity: 1;
    background-position: 0 -120%;
  }

  100% {
    opacity: 0;
    background-position: 0 120%;
  }
}

@keyframes ck-swap {
  from {
    opacity: 0.2;
    transform: translateY(7px);
  }
}

@keyframes ck-spine-run {
  0% {
    top: 0;
    opacity: 0;
  }

  8% {
    opacity: 1;
  }

  92% {
    opacity: 1;
  }

  100% {
    top: calc(100% - 64px);
    opacity: 0;
  }
}

/* ============================ 响应式 ============================
   分辨率适配统一交给外层 AutoFit 容器（1920×1080 设计稿等比缩放、锚定左上、
   设计面自动撑满视口）：任意分辨率下面板占位比例恒定、四边贴满。
   因此这里不再写任何视口媒体查询 —— 缩放子树里的 vw/vh 与 @media 视口
   条件都指向真实视口而非设计面，写了必错；新尺寸一律用固定设计像素。 */

/* 尊重系统的"减弱动效"偏好 */
@media (prefers-reduced-motion: reduce) {

  .ck *,
  .ck *::before,
  .ck *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
  }

  .ck-beam {
    display: none;
  }
}
</style>
